import { FC, useState } from "react";

import { Loader } from "shared/components/Loader";
import { DecisionCriteriaRadio } from "./components/DecisionCriteriaRadio";
import { ScoreCriteria } from "./slices/ScoreCriteria";
import { useTemplateContext } from "../../provider";
import { RuleCriteria } from "./slices/RuleCriteria";
import { Show } from "shared/components/helpers/Show";
import {
  ApplicantSettingsErrors,
  RecommendationMethod,
  VerificationSchemas,
  VerificationSettings
} from "types/landlord/verificationTemplate";
import { Card } from "shared/components/Card";
import { CheckComponent } from "../../components/CheckComponent";
import { ApplicantType, ExecutionModes, VerificationNames } from "shared/types";
import { RevertConditionallyRunModal } from "shared/app/VerificationTemplate/slices/DecisionCriteria/components/RevertConditionallyRunModal";

export const DecisionCriteria: FC<{
  applicantSettingsErrors?: ApplicantSettingsErrors | null;
  onApplicantSettingsErrorsUpdate?: (
    errors: ApplicantSettingsErrors | null
  ) => void;
}> = ({ applicantSettingsErrors, onApplicantSettingsErrorsUpdate }) => {
  const [decisionModalMode, setDecisionModalMode] = useState<
    RecommendationMethod | undefined
  >(undefined);

  const { template, verificationSchemas, setTemplate, readOnly } =
    useTemplateContext();

  const criteria = template?.screening_criteria;
  const templateVerificationSchemas = template?.verification_schemas;

  const setCriteriaEnabled = (v: RecommendationMethod) => {
    template &&
      setTemplate({
        ...template,
        recommendation_method: v
      });
  };

  const getConditionallyExecutedReports = () => {
    const conditionalVerificationSchemas = Object.entries(
      template?.verification_schemas || {}
    ).reduce((vs: VerificationSchemas, [partnerType, verifications]) => {
      vs[partnerType] = Object.entries(verifications).reduce(
        // @ts-expect-error
        (vNames, ret: [string, VerificationSettings]) => {
          if (
            ret[1].execution_mode === ExecutionModes.Conditionally &&
            [
              VerificationNames.EvictionReport,
              VerificationNames.CriminalReport
            ].includes(ret[0] as VerificationNames)
          )
            return [...vNames, ret[0] as VerificationNames];

          return vNames;
        },
        [] as VerificationNames[]
      );

      return vs;
    }, {} as VerificationSchemas);

    return {
      perPartnerTypeReports: conditionalVerificationSchemas,
      total: Object.values(conditionalVerificationSchemas).reduce(
        (acc, reports) => acc + Object.values(reports).length,
        0
      )
    };
  };

  const { perPartnerTypeReports, total } = getConditionallyExecutedReports();

  const handleSwitchAllConditionallyToAutomatic = () => {
    if (!template) return;

    const data = Object.entries(
      perPartnerTypeReports as Record<ApplicantType, VerificationNames[]>
    ).reduce((acc, [partnerType, reports]) => {
      if (reports.length > 0) {
        const adjustedReports = reports.reduce(
          (acc, key) => ({
            ...acc,
            [key]: {
              ...template.verification_schemas[partnerType][key],
              execution_mode: ExecutionModes.Automatic
            }
          }),
          {}
        );

        return {
          ...acc,
          [partnerType]: {
            ...template.verification_schemas[partnerType],
            ...adjustedReports
          }
        };
      }

      return acc;
    }, {});

    const updatedTemplate = {
      ...template,
      recommendation_method: decisionModalMode,
      verification_schemas: {
        ...template.verification_schemas,
        ...data
      }
    };

    setTemplate(updatedTemplate);
    setDecisionModalMode(undefined);
  };

  const handleChangeCriteria = (decision: RecommendationMethod) => {
    if (total > 0) setDecisionModalMode(decision);
    else setCriteriaEnabled(decision);
  };

  if (!criteria)
    return (
      <div className="mx-auto pt-10">
        <Loader />
      </div>
    );

  if (!templateVerificationSchemas)
    return <p>No template verification schemas</p>;

  return (
    <div className="animate-appear pt-2 pb-6">
      <Show if={!readOnly}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[150px]">
          <DecisionCriteriaRadio
            decisionRecommendation={template.recommendation_method || "none"}
            value="none"
            setDecision={handleChangeCriteria}
            title="Do not recommend decision"
            subTitle="Do not recommend decisions for applications"
          />

          <DecisionCriteriaRadio
            decisionRecommendation={template.recommendation_method || "none"}
            value="rule_based"
            setDecision={handleChangeCriteria}
            title="Rule-based recommendation"
            subTitle="Set rules for when to approve applications"
          />

          <DecisionCriteriaRadio
            decisionRecommendation={template.recommendation_method || "none"}
            value="score_based"
            setDecision={handleChangeCriteria}
            title="Score-based recommendation"
            subTitle="Set criteria. Recommend decision based on calculated score"
          />
        </div>
      </Show>
      <Show
        if={Boolean(
          template.recommendation_method === "score_based" &&
            verificationSchemas
        )}
      >
        <Show if={!readOnly}>
          <div className="h-px bg-alpha-40 my-6" />
        </Show>
        <ScoreCriteria
          verificationSchemas={verificationSchemas!}
          applicantSettingsErrors={applicantSettingsErrors}
          onApplicantSettingsErrorsUpdate={onApplicantSettingsErrorsUpdate}
        />
      </Show>

      <Show if={Boolean(template.recommendation_method === "rule_based")}>
        <div className="h-px bg-alpha-40 my-6" />
        <RuleCriteria />
      </Show>

      <Show
        if={
          Boolean(
            !template.recommendation_method ||
              template.recommendation_method === "none"
          ) && readOnly
        }
      >
        <Card className="h-[88px] flex items-center px-8">
          <CheckComponent
            checked={true}
            labelClassName="!text-base !font-bold !text-content-1000"
            label="Do not recommend decisions for applications"
          />
        </Card>
      </Show>

      <RevertConditionallyRunModal
        isOpen={Boolean(decisionModalMode)}
        onSubmit={() => handleSwitchAllConditionallyToAutomatic()}
        onClose={() => setDecisionModalMode(undefined)}
      />
    </div>
  );
};
